import React, { useMemo } from "react";
import { INQUIRY_TYPES, TYPE_TONE } from "../constants";
import {
  classNames,
  firstResponseMinutes,
  formatDuration,
  formatRelative,
} from "./utils";

function Card({ title, subtitle, children, rightSlot }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-card">
      <header className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5">
        <div>
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            {title}
          </h3>
          {subtitle ? (
            <p className="text-[11px] text-zinc-500">{subtitle}</p>
          ) : null}
        </div>
        {rightSlot}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function BarRow({ label, value, max, tone = "bg-indigo-500", chip }) {
  const pct = max ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="w-28 shrink-0 text-xs font-medium text-zinc-700">
        <span className="inline-flex items-center gap-1.5">
          {chip}
          <span className="truncate">{label}</span>
        </span>
      </div>
      <div className="relative flex-1">
        <div className="h-2 overflow-hidden rounded-full bg-zinc-100">
          <div className={classNames("h-full rounded-full", tone)} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="w-10 shrink-0 text-right text-xs font-semibold tabular-nums text-zinc-800">
        {value}
      </div>
    </div>
  );
}

function TrendSparkline({ days }) {
  const max = days.reduce((m, d) => Math.max(m, d.count), 0) || 1;
  return (
    <div className="flex h-20 items-end gap-1">
      {days.map((d, i) => (
        <div
          key={`${d.label}-${i}`}
          className="flex flex-1 flex-col items-center gap-1"
          title={`${d.label}: ${d.count}`}
        >
          <div
            className={classNames(
              "w-full rounded-sm",
              d.count ? "bg-indigo-500" : "bg-zinc-200"
            )}
            style={{ height: `${Math.max(4, (d.count / max) * 70)}px` }}
          />
          <span className="text-[9px] text-zinc-500">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function last14Days(inquiries) {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 13; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    days.push({
      ts: d.getTime(),
      label: d.toLocaleDateString(undefined, { month: "numeric", day: "numeric" }),
      count: 0,
    });
  }
  for (const inq of inquiries || []) {
    const ms = inq?.createdAt ? new Date(inq.createdAt).getTime() : 0;
    if (!ms) continue;
    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    const idx = days.findIndex((x) => x.ts === d.getTime());
    if (idx >= 0) days[idx].count += 1;
  }
  return days;
}

// Quartile buckets of first-response time for the last 30 days.
function responseTimeDistribution(inquiries) {
  const now = Date.now();
  const buckets = [
    { id: "lt15", label: "<15m", min: 0, max: 15, count: 0 },
    { id: "lt1h", label: "15m–1h", min: 15, max: 60, count: 0 },
    { id: "lt4h", label: "1h–4h", min: 60, max: 240, count: 0 },
    { id: "lt24h", label: "4h–24h", min: 240, max: 1440, count: 0 },
    { id: "gt24h", label: ">24h", min: 1440, max: Infinity, count: 0 },
  ];
  let total = 0;
  for (const inq of inquiries || []) {
    const createdMs = inq?.createdAt ? new Date(inq.createdAt).getTime() : 0;
    if (!createdMs || now - createdMs > 30 * 86400000) continue;
    const frm = firstResponseMinutes(inq);
    if (frm == null) continue;
    const b = buckets.find((x) => frm >= x.min && frm < x.max);
    if (b) {
      b.count += 1;
      total += 1;
    }
  }
  return { buckets, total };
}

export default function AnalyticsView({ inquiries, analytics, onClose }) {
  const typeTotals = analytics?.byType || {};
  const typeMax = Math.max(1, ...INQUIRY_TYPES.map((t) => typeTotals[t] || 0));
  const responderMax = Math.max(1, ...(analytics?.byResponder || []).map((r) => r.count));

  const trendDays = useMemo(() => last14Days(inquiries), [inquiries]);
  const dist = useMemo(() => responseTimeDistribution(inquiries), [inquiries]);

  const distMax = Math.max(1, ...dist.buckets.map((b) => b.count));

  return (
    <section className="flex min-w-0 flex-1 flex-col bg-zinc-50/60">
      <header className="flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
            Insights
          </p>
          <h2 className="text-sm font-semibold text-zinc-900">Inquiry analytics</h2>
        </div>
        <button
          onClick={onClose}
          className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
        >
          Back to list
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <Card
            title="Volume · last 14 days"
            subtitle={`${trendDays.reduce((s, d) => s + d.count, 0)} inquiries received`}
          >
            <TrendSparkline days={trendDays} />
          </Card>

          <Card
            title="First-response time"
            subtitle={`${dist.total} replied inquiries in the last 30 days`}
            rightSlot={
              analytics?.avgFirstResponseMinutes != null ? (
                <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-200">
                  Avg {formatDuration(analytics.avgFirstResponseMinutes)}
                </span>
              ) : null
            }
          >
            {dist.total === 0 ? (
              <p className="text-xs text-zinc-500">
                Not enough replied inquiries in the last 30 days to compute.
              </p>
            ) : (
              <div>
                {dist.buckets.map((b) => (
                  <BarRow
                    key={b.id}
                    label={b.label}
                    value={b.count}
                    max={distMax}
                    tone={
                      b.id === "gt24h"
                        ? "bg-red-500"
                        : b.id === "lt24h"
                        ? "bg-amber-500"
                        : b.id === "lt4h"
                        ? "bg-indigo-500"
                        : "bg-emerald-500"
                    }
                  />
                ))}
              </div>
            )}
          </Card>

          <Card title="Breakdown by type" subtitle="All time">
            {INQUIRY_TYPES.map((t) => {
              const tone = TYPE_TONE[t] || TYPE_TONE.Other;
              return (
                <BarRow
                  key={t}
                  label={t}
                  value={typeTotals[t] || 0}
                  max={typeMax}
                  tone={tone.dot}
                  chip={
                    <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", tone.dot)} />
                  }
                />
              );
            })}
          </Card>

          <Card
            title="Top responders"
            subtitle="Replies in the last 30 days"
          >
            {(analytics?.byResponder?.length || 0) === 0 ? (
              <p className="text-xs text-zinc-500">No replies logged in the last 30 days.</p>
            ) : (
              analytics.byResponder.slice(0, 8).map((r) => (
                <BarRow
                  key={r.name}
                  label={r.name}
                  value={r.count}
                  max={responderMax}
                  tone="bg-indigo-500"
                />
              ))
            )}
          </Card>

          <Card title="Status mix" subtitle="All inquiries">
            {[
              { id: "new", label: "New / unreplied", tone: "bg-emerald-500" },
              { id: "replied", label: "Replied · open", tone: "bg-indigo-500" },
              { id: "closed", label: "Closed", tone: "bg-zinc-400" },
            ].map((s) => (
              <BarRow
                key={s.id}
                label={s.label}
                value={analytics?.byStatus?.[s.id] || 0}
                max={Math.max(1, analytics?.total || 0)}
                tone={s.tone}
              />
            ))}
          </Card>

          <Card title="Attention queue" subtitle="Unreplied inquiries getting older">
            {(analytics?.buckets?.overdue || 0) +
              (analytics?.buckets?.stale || 0) ===
            0 ? (
              <p className="text-xs text-zinc-500">Nothing stale — inbox is healthy.</p>
            ) : (
              <div>
                <BarRow
                  label=">1 day"
                  value={analytics.buckets.overdue || 0}
                  max={Math.max(1, analytics.byStatus?.new || 1)}
                  tone="bg-red-500"
                />
                <BarRow
                  label="4h – 1d"
                  value={analytics.buckets.stale || 0}
                  max={Math.max(1, analytics.byStatus?.new || 1)}
                  tone="bg-amber-500"
                />
                <BarRow
                  label="1 – 4h"
                  value={analytics.buckets.fresh || 0}
                  max={Math.max(1, analytics.byStatus?.new || 1)}
                  tone="bg-indigo-500"
                />
                <BarRow
                  label="<1h"
                  value={analytics.buckets.new || 0}
                  max={Math.max(1, analytics.byStatus?.new || 1)}
                  tone="bg-emerald-500"
                />
                {analytics.oldestUnreplied ? (
                  <p className="mt-2 text-[11px] text-zinc-500">
                    Oldest:{" "}
                    <span className="font-medium text-zinc-800">
                      {analytics.oldestUnreplied.name || analytics.oldestUnreplied.email}
                    </span>{" "}
                    · {formatRelative(analytics.oldestUnreplied.createdAt)}
                  </p>
                ) : null}
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
}
